"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Pencil, Plus, ChevronDown, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter } from "@/components/ui/drawer";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";
import { useStoreLocation } from "@/context/store-location-context";
import { useCartStore } from "@/lib/cart/cart-store";
import { trackAction } from "@/lib/track-action";
import AddAddressForm from "./add-address-form";
import EditAddressForm from "./edit-address-form";

const ADDRESSES_PER_PAGE = 8;

type Address = {
  id: number | string;
  label?: string;
  full_address: string;
  city?: string;
  microhub_id?: string | number;
};

type AddressChooserProps = {
  selectedAddressId: string | number | null;
  onSelectAddress: (addressId: string | number, address: Address) => void;
  onAddressesLoaded?: (addresses: Address[]) => void;
  onAddressAdded?: () => void;
  listStyle?: boolean;
};

export default function AddressChooser({
  selectedAddressId,
  onSelectAddress,
  onAddressesLoaded,
  onAddressAdded,
  listStyle = false,
}: AddressChooserProps) {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(ADDRESSES_PER_PAGE);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | number | null>(null);
  const [pendingAddress, setPendingAddress] = useState<{ id: string | number; address: Address } | null>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { storeLocation } = useStoreLocation();
  const clearCart = useCartStore((s) => s.clearCart);
  const router = useRouter();

  const fetchAddresses = useCallback(async (): Promise<Address[]> => {
    setIsLoading(true);
    const result = await clientAPI.get<{ shipping_infos: Address[] }>(API_ENDPOINTS.USER_ADDRESSES);
    const fetched = result.ok && result.data?.shipping_infos ? result.data.shipping_infos : [];
    setAddresses(fetched);
    onAddressesLoaded?.(fetched);
    setIsLoading(false);
    return fetched;
  }, [onAddressesLoaded]);

  useEffect(() => {
    fetchAddresses();
  }, []);

  const visibleAddresses = addresses.slice(0, visibleCount);
  const hasMore = visibleCount < addresses.length;
  const remainingCount = addresses.length - visibleCount;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + ADDRESSES_PER_PAGE);
  };

  const handleAddressClick = (addressId: string | number, address: Address) => {
    const currentCity = storeLocation.city;
    if (currentCity && address.city && address.city !== currentCity) {
      setPendingAddress({ id: addressId, address });
    } else {
      onSelectAddress(addressId, address);
    }
  };

  const handleCityChangeConfirm = async () => {
    if (!pendingAddress) return;
    trackAction("SWITCH-CITY-ACCEPTED", pendingAddress.address.city ?? "");
    onSelectAddress(pendingAddress.id, pendingAddress.address);
    clearCart();
    setPendingAddress(null);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    window.location.href = "/";
  };

  const handleAddAddressSuccess = async (newAddressId: string | number) => {
    setShowAddAddress(false);
    const freshAddresses = await fetchAddresses();
    const newAddress = freshAddresses.find((a) => a.id === newAddressId);
    if (newAddress) handleAddressClick(newAddressId, newAddress);
    onAddressAdded?.();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {visibleAddresses.map((address, index) => (
        <div key={address.id}>
          {listStyle && index > 0 && <Separator />}
        <div
          onClick={() => handleAddressClick(address.id, address)}
          className={cn(
            "flex items-center gap-3 px-2 cursor-pointer transition",
            listStyle ? "py-2" : "py-1 rounded-lg",
            listStyle && address.id === selectedAddressId ? "bg-gray-100" : "hover:bg-gray-50"
          )}
        >
          {listStyle ? (
            <MapPin className={cn(
              "h-5 w-5 flex-shrink-0 mt-0.5",
              address.id === selectedAddressId ? "text-teal-500" : "text-muted-foreground"
            )} />
          ) : (
            <div
              className={cn(
                "h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                address.id === selectedAddressId ? "border-teal-500" : "border-gray-300"
              )}
            >
              {address.id === selectedAddressId && (
                <div className="h-2.5 w-2.5 rounded-full bg-teal-500" />
              )}
            </div>
          )}

          {/* Address details */}
          <div className="flex-1 min-w-0">
            {address.label && <p className="font-medium text-sm">{address.label}</p>}
            <p className={cn("text-sm truncate", address.label ? "text-muted-foreground" : "")}>
              {address.full_address}
            </p>
          </div>

          {/* Edit button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditingAddressId(address.id);
            }}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-gray-100 rounded-md transition cursor-pointer flex-shrink-0"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
        </div>
      ))}

      {/* Show more button */}
      {hasMore && (
        <div className="px-4">
          <Button
            variant="ghost"
            className="w-full gap-2 text-muted-foreground mb-2 cursor-pointer"
            onClick={handleShowMore}
          >
            <ChevronDown className="h-4 w-4" />
            Ver más ({remainingCount})
          </Button>
        </div>
      )}

      {/* Add new address button */}
      <div className="px-4">
        <Button
          variant="outline"
          className="w-full gap-2 cursor-pointer"
          onClick={() => setShowAddAddress(true)}
        >
          <Plus className="h-4 w-4" />
          Agregar nueva dirección
        </Button>
      </div>

      {/* City change confirmation dialog */}
      <Dialog open={!!pendingAddress} onOpenChange={(open) => { if (!open) { trackAction("SWITCH-CITY-REJECTED", pendingAddress?.address.city ?? ""); setPendingAddress(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambio de ciudad</DialogTitle>
            <DialogDescription>
              ¿Estás seguro que deseas cambiar de ciudad? Tendrás que empezar de nuevo tu pedido.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { trackAction("SWITCH-CITY-REJECTED", pendingAddress?.address.city ?? ""); setPendingAddress(null); }} className="cursor-pointer">
              Cancelar
            </Button>
            <Button onClick={handleCityChangeConfirm} className="bg-red-500 hover:bg-red-600 cursor-pointer">
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit address dialog (desktop) / drawer (mobile) */}
      {isDesktop ? (
        <Dialog open={!!editingAddressId} onOpenChange={(open) => !open && setEditingAddressId(null)}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden pr-0">
            <DialogHeader className="pr-6">
              <DialogTitle>Editar dirección</DialogTitle>
            </DialogHeader>
            {editingAddressId && (
              <EditAddressForm
                addressId={editingAddressId}
                onSuccess={async () => { setEditingAddressId(null); await fetchAddresses(); }}
                onCancel={() => setEditingAddressId(null)}
                renderFooter={({ onSubmit, onCancel, isSubmitting }) => (
                  <DialogFooter className="flex-shrink-0 pt-4 border-t bg-white pr-6">
                    <Button variant="outline" onClick={onCancel} className="cursor-pointer">
                      Cancelar
                    </Button>
                    <Button
                      onClick={onSubmit}
                      disabled={isSubmitting}
                      className="bg-teal-400 hover:bg-teal-500 cursor-pointer"
                    >
                      {isSubmitting ? "Guardando..." : "Guardar dirección"}
                    </Button>
                  </DialogFooter>
                )}
              />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={!!editingAddressId} onOpenChange={(open) => !open && setEditingAddressId(null)}>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader>
              <DrawerTitle>Editar dirección</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 flex-1 min-h-0 flex flex-col">
              {editingAddressId && (
                <EditAddressForm
                  addressId={editingAddressId}
                  onSuccess={async () => { setEditingAddressId(null); await fetchAddresses(); }}
                  onCancel={() => setEditingAddressId(null)}
                  renderFooter={({ onSubmit, isSubmitting }) => (
                    <div className="flex-shrink-0 pt-4 pb-8 border-t bg-white">
                      <Button
                        onClick={onSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
                      >
                        {isSubmitting ? "Guardando..." : "Guardar dirección"}
                      </Button>
                    </div>
                  )}
                />
              )}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {/* Add address dialog (desktop) / drawer (mobile) */}
      {isDesktop ? (
        <Dialog open={showAddAddress} onOpenChange={setShowAddAddress}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden pr-0">
            <DialogHeader className="pr-6">
              <DialogTitle>Agregar nueva dirección</DialogTitle>
            </DialogHeader>
            <AddAddressForm
              onSuccess={handleAddAddressSuccess}
              onCancel={() => setShowAddAddress(false)}
              renderFooter={({ onBack, onSubmit, onCancel, isSubmitting, showBack }) => (
                <DialogFooter className="flex-shrink-0 pt-4 border-t bg-white pr-6">
                  {showBack && (
                    <Button variant="outline" onClick={onBack} className="cursor-pointer" disabled={isSubmitting}>
                      Atrás
                    </Button>
                  )}
                  {!showBack && (
                    <Button variant="outline" onClick={onCancel} className="cursor-pointer">
                      Cancelar
                    </Button>
                  )}
                  {showBack && (
                    <Button
                      onClick={onSubmit}
                      disabled={isSubmitting}
                      className="bg-teal-400 hover:bg-teal-500 cursor-pointer"
                    >
                      {isSubmitting ? "Guardando..." : "Guardar dirección"}
                    </Button>
                  )}
                </DialogFooter>
              )}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={showAddAddress} onOpenChange={setShowAddAddress}>
          <DrawerContent className="min-h-[75vh] max-h-[90vh]">
            <DrawerHeader>
              <DrawerTitle>Agregar nueva dirección</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 flex-1 min-h-0 flex flex-col">
              <AddAddressForm
                onSuccess={handleAddAddressSuccess}
                onCancel={() => setShowAddAddress(false)}
                renderFooter={({ onBack, onSubmit, isSubmitting, showBack }) => (
                  showBack ? (
                    <div className="flex-shrink-0 pt-4 pb-8 border-t bg-white">
                      <Button
                        onClick={onSubmit}
                        disabled={isSubmitting}
                        className="w-full bg-teal-400 hover:bg-teal-500 cursor-pointer"
                      >
                        {isSubmitting ? "Guardando..." : "Guardar dirección"}
                      </Button>
                    </div>
                  ) : null
                )}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}

export type { Address };
